import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'
import { formatCurrency } from '../../lib/format'

interface Account {
  id: number
  name: string
  slug: string
}

interface Sale {
  id: number
  total: number
  tax_amount: number
  status: string
  payment_method: string
  created_at: string
  customer?: { id: number; name: string }
  user?: { id: number; email_address: string }
}

interface Props {
  sales: Sale[]
  account: Account
  permissions?: {
    can_manage_sales: boolean
  }
  can_delete?: boolean
}

export default function SalesIndex({ sales, account, permissions, can_delete }: Props) {
  const todayStr = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD in local tz
  const [from, setFrom] = useState(todayStr)
  const [to, setTo] = useState(todayStr)

  const statusColors: Record<string, string> = {
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-red-100 text-red-800',
  }

  const statusLabels: Record<string, string> = {
    completed: 'Completada',
    pending: 'Pendiente',
    cancelled: 'Cancelada',
  }

  const paymentLabels: Record<string, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    other: 'Otro',
  }

  const handleFilter = () => {
    router.get(`/${account.slug}/sales`, { from, to }, { preserveState: true, replace: true })
  }

  const handleClear = () => {
    setFrom(todayStr)
    setTo(todayStr)
    router.get(`/${account.slug}/sales`, { from: todayStr, to: todayStr }, { preserveState: true, replace: true })
  }

  return (
    <Layout account={account} permissions={permissions}>
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#202020]">Ventas</h1>
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            />
          </div>
          <div className="flex-1 w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
            />
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <button
              onClick={handleFilter}
              className="flex-1 md:flex-none bg-[#55ACEE] text-white px-4 py-2 rounded hover:bg-[#4a9dd4] transition-colors"
            >
              Filtrar
            </button>
            <button
              onClick={handleClear}
              className="flex-1 md:flex-none bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition-colors"
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fecha</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Cliente</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">ISV</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Total</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pago</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sales.map((sale) => (
              <tr key={sale.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link href={`/${account.slug}/sales/${sale.id}`} className="text-[#55ACEE] hover:underline font-mono">#{sale.id}</Link>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm text-[#202020] whitespace-nowrap">{new Date(sale.created_at).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City' })}</p>
                  <p className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(sale.created_at).toLocaleTimeString('es-MX', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </td>
                <td className="px-4 py-3">{sale.customer?.name || 'General'}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(Number(sale.tax_amount || 0))}</td>
                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatCurrency(Number(sale.total))}</td>
                <td className="px-4 py-3 whitespace-nowrap">{paymentLabels[sale.payment_method] || sale.payment_method}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${statusColors[sale.status] || ''}`}>
                    {statusLabels[sale.status] || sale.status}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <Link href={`/${account.slug}/sales/${sale.id}`} className="text-[#55ACEE] hover:underline text-sm">
                    Ver detalle
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </Layout>
  )
}
